import { useAppStore } from "@/store/app";
import {
  fetchAssociationsWithSwap,
  generateCacheKey,
  transformAssociations,
  type AssociationPickerOption,
} from "@/util/associationUtils";
import { ASSOCIATION_CONSTANTS } from "@/util/constants";
import { computed, ref, type Ref } from "vue";
import { useI18n } from "vue-i18n";

// Re-exported for existing consumers that import the type from this composable.
export type { AssociationPickerOption } from "@/util/associationUtils";

/**
 * Shared association data logic used by both the contact-association dialog and the
 * unified opportunity setup modal. Fetches associations between `objectModel` and
 * `withObjectModel` (defaults to contact), with a swapped-direction retry in pair
 * mode, caches the result, and exposes transformed dropdown options carrying the
 * first/second object keys.
 */
export function useAssociationOptions(
  objectModel: Ref<string | undefined>,
  withObjectModel: Ref<string | undefined>
) {
  const store = useAppStore();
  const { t } = useI18n();

  const options = ref<AssociationPickerOption[]>([]);
  const loading = ref(false);
  const error = ref("");

  /** The object this association is configured against (defaults to contact). */
  const targetObjectModel = computed(
    () => withObjectModel.value || ASSOCIATION_CONSTANTS.CONTACT_OBJECT_TYPE
  );

  /** True when linking two non-contact objects. */
  const isObjectPairMode = computed(
    () => targetObjectModel.value !== ASSOCIATION_CONSTANTS.CONTACT_OBJECT_TYPE
  );

  /** Namespaced cache-key model so pair associations never collide with contact ones. */
  const cacheModelKey = computed(() =>
    isObjectPairMode.value
      ? `${objectModel.value}__${targetObjectModel.value}`
      : objectModel.value
  );

  /** Fetch associations from cache or API. */
  const fetchAssociations = async (): Promise<void> => {
    if (!objectModel.value || !store.locationId || loading.value) {
      return;
    }

    const cacheKey = generateCacheKey(cacheModelKey.value, store.locationId);
    const cached = store.associationCache.get(cacheKey);
    const isCacheExpired =
      cached && Date.now() - cached.timestamp > ASSOCIATION_CONSTANTS.CACHE_EXPIRY_MS;
    if (cached && !isCacheExpired) {
      options.value = cached.associations;
      return;
    }

    try {
      loading.value = true;
      error.value = "";
      const associations = await fetchAssociationsWithSwap(
        objectModel.value,
        targetObjectModel.value,
        store.locationId
      );
      options.value = transformAssociations(
        associations,
        store.customObjects || [],
        t
      );
      store.associationCache.set(cacheKey, {
        associations: options.value,
        timestamp: Date.now(),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch associations:", err);
      error.value = t("common.failedToLoadAssociations");
      options.value = [];
    } finally {
      loading.value = false;
    }
  };

  /** Find a fetched option by its association id. */
  const findOption = (id?: string): AssociationPickerOption | undefined =>
    options.value.find((option) => option.value === id);

  return {
    options,
    loading,
    error,
    targetObjectModel,
    isObjectPairMode,
    cacheModelKey,
    fetchAssociations,
    findOption,
  };
}
